/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { toolsetsAddExternalOAuthServer } from "../funcs/toolsetsAddExternalOAuthServer.js";
import { toolsetsCheckMCPSlugAvailability } from "../funcs/toolsetsCheckMCPSlugAvailability.js";
import { toolsetsCloneBySlug } from "../funcs/toolsetsCloneBySlug.js";
import { toolsetsCreate } from "../funcs/toolsetsCreate.js";
import { toolsetsDeleteBySlug } from "../funcs/toolsetsDeleteBySlug.js";
import { toolsetsGetBySlug } from "../funcs/toolsetsGetBySlug.js";
import { toolsetsList } from "../funcs/toolsetsList.js";
import { toolsetsListForOrg } from "../funcs/toolsetsListForOrg.js";
import { toolsetsListToolFilters } from "../funcs/toolsetsListToolFilters.js";
import { toolsetsListToolSchemaStaticValues } from "../funcs/toolsetsListToolSchemaStaticValues.js";
import { toolsetsRemoveOAuthServer } from "../funcs/toolsetsRemoveOAuthServer.js";
import { toolsetsSetToolVariationsGroup } from "../funcs/toolsetsSetToolVariationsGroup.js";
import { toolsetsSetUserSessionIssuer } from "../funcs/toolsetsSetUserSessionIssuer.js";
import { toolsetsUpdateBySlug } from "../funcs/toolsetsUpdateBySlug.js";
import { toolsetsUpdateExternalOAuthServer } from "../funcs/toolsetsUpdateExternalOAuthServer.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { ListToolFiltersResult } from "../models/components/listtoolfiltersresult.js";
import { ListToolSchemaStaticValuesResult } from "../models/components/listtoolschemastaticvaluesresult.js";
import { ListToolsetsResult } from "../models/components/listtoolsetsresult.js";
import { ListToolsetSummariesResult } from "../models/components/listtoolsetsummariesresult.js";
import { Toolset } from "../models/components/toolset.js";
import {
  AddExternalOAuthServerRequest,
  AddExternalOAuthServerSecurity,
} from "../models/operations/addexternaloauthserver.js";
import {
  CheckMCPSlugAvailabilityRequest,
  CheckMCPSlugAvailabilitySecurity,
} from "../models/operations/checkmcpslugavailability.js";
import {
  CloneToolsetRequest,
  CloneToolsetSecurity,
} from "../models/operations/clonetoolset.js";
import {
  CreateToolsetRequest,
  CreateToolsetSecurity,
} from "../models/operations/createtoolset.js";
import {
  DeleteToolsetRequest,
  DeleteToolsetSecurity,
} from "../models/operations/deletetoolset.js";
import {
  GetToolsetRequest,
  GetToolsetSecurity,
} from "../models/operations/gettoolset.js";
import {
  ListToolsetsRequest,
  ListToolsetsSecurity,
} from "../models/operations/listtoolsets.js";
import {
  ListToolsetsForOrgRequest,
  ListToolsetsForOrgSecurity,
} from "../models/operations/listtoolsetsfororg.js";
import {
  ListToolsetToolFiltersRequest,
  ListToolsetToolFiltersSecurity,
} from "../models/operations/listtoolsettoolfilters.js";
import {
  ListToolsetToolSchemaStaticValuesRequest,
  ListToolsetToolSchemaStaticValuesSecurity,
} from "../models/operations/listtoolsettoolschemastaticvalues.js";
import {
  RemoveOAuthServerRequest,
  RemoveOAuthServerSecurity,
} from "../models/operations/removeoauthserver.js";
import {
  SetToolsetToolVariationsGroupRequest,
  SetToolsetToolVariationsGroupSecurity,
} from "../models/operations/settoolsettoolvariationsgroup.js";
import {
  SetToolsetUserSessionIssuerRequest,
  SetToolsetUserSessionIssuerSecurity,
} from "../models/operations/settoolsetusersessionissuer.js";
import {
  UpdateExternalOAuthServerRequest,
  UpdateExternalOAuthServerSecurity,
} from "../models/operations/updateexternaloauthserver.js";
import {
  UpdateToolsetRequest,
  UpdateToolsetSecurity,
} from "../models/operations/updatetoolset.js";
import { unwrapAsync } from "../types/fp.js";

export class Toolsets extends ClientSDK {
  /**
   * addExternalOAuthServer toolsets
   *
   * @remarks
   * Associate an external OAuth server with a toolset
   */
  async addExternalOAuthServer(
    request: AddExternalOAuthServerRequest,
    security?: AddExternalOAuthServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsAddExternalOAuthServer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * checkMCPSlugAvailability toolsets
   *
   * @remarks
   * Check if a MCP slug is taken in any scope (true means taken).
   *
   * Deprecated: use mcpEndpoints.checkSlugAvailability instead, which is scoped to one domain and returns true when available.
   *
   * @deprecated method: This will be removed in a future release, please migrate away from it as soon as possible.
   */
  async checkMCPSlugAvailability(
    request: CheckMCPSlugAvailabilityRequest,
    security?: CheckMCPSlugAvailabilitySecurity | undefined,
    options?: RequestOptions,
  ): Promise<boolean> {
    return unwrapAsync(toolsetsCheckMCPSlugAvailability(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * cloneToolset toolsets
   *
   * @remarks
   * Clone an existing toolset with a new name
   */
  async cloneBySlug(
    request: CloneToolsetRequest,
    security?: CloneToolsetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsCloneBySlug(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createToolset toolsets
   *
   * @remarks
   * Create a new toolset with associated tools
   */
  async create(
    request: CreateToolsetRequest,
    security?: CreateToolsetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteToolset toolsets
   *
   * @remarks
   * Delete a toolset by its ID
   */
  async deleteBySlug(
    request: DeleteToolsetRequest,
    security?: DeleteToolsetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(toolsetsDeleteBySlug(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getToolset toolsets
   *
   * @remarks
   * Get detailed information about a toolset including full HTTP tool definitions
   */
  async getBySlug(
    request: GetToolsetRequest,
    security?: GetToolsetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsGetBySlug(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listToolsets toolsets
   *
   * @remarks
   * List all toolsets for a project
   */
  async list(
    request?: ListToolsetsRequest | undefined,
    security?: ListToolsetsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListToolsetsResult> {
    return unwrapAsync(toolsetsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listToolsetsForOrg toolsets
   *
   * @remarks
   * List all toolsets across the organization (summary view)
   */
  async listForOrg(
    request?: ListToolsetsForOrgRequest | undefined,
    security?: ListToolsetsForOrgSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListToolsetSummariesResult> {
    return unwrapAsync(toolsetsListForOrg(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listToolFilters toolsets
   *
   * @remarks
   * List the tool filter scopes (tags) available on a toolset-backed MCP server and the tools under each, including tools excluded from all filters. Read-only; reflects the explicit tool variations group configured on the toolset, deriving effective tags with the same logic as the runtime ?tags= filter. Returns filtering disabled when no explicit group is set.
   */
  async listToolFilters(
    request: ListToolsetToolFiltersRequest,
    security?: ListToolsetToolFiltersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListToolFiltersResult> {
    return unwrapAsync(toolsetsListToolFilters(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listToolSchemaStaticValues toolsets
   *
   * @remarks
   * List every const, default, enum, example, and examples value in each tool input schema. The result is deliberately mechanical: clients decide how to present or interpret the values. This supports reviewing the exact schema contents before changing how a toolset is shared.
   */
  async listToolSchemaStaticValues(
    request: ListToolsetToolSchemaStaticValuesRequest,
    security?: ListToolsetToolSchemaStaticValuesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListToolSchemaStaticValuesResult> {
    return unwrapAsync(toolsetsListToolSchemaStaticValues(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * removeOAuthServer toolsets
   *
   * @remarks
   * Remove OAuth server association from a toolset
   */
  async removeOAuthServer(
    request: RemoveOAuthServerRequest,
    security?: RemoveOAuthServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsRemoveOAuthServer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setToolVariationsGroup toolsets
   *
   * @remarks
   * Assign a tool variations group to a toolset to enable MCP tool filtering (or pass null to disable). The group must already exist in the caller's project.
   */
  async setToolVariationsGroup(
    request: SetToolsetToolVariationsGroupRequest,
    security?: SetToolsetToolVariationsGroupSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsSetToolVariationsGroup(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setUserSessionIssuer toolsets
   *
   * @remarks
   * Link a toolset to a user_session_issuer (or pass null to unlink). The user_session_issuer must already exist in the caller's project.
   *
   * Deprecated: use mcpServers.update instead.
   *
   * @deprecated method: This will be removed in a future release, please migrate away from it as soon as possible.
   */
  async setUserSessionIssuer(
    request: SetToolsetUserSessionIssuerRequest,
    security?: SetToolsetUserSessionIssuerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsSetUserSessionIssuer(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateToolset toolsets
   *
   * @remarks
   * Update a toolset's properties including name, description, and HTTP tools
   */
  async updateBySlug(
    request: UpdateToolsetRequest,
    security?: UpdateToolsetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsUpdateBySlug(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateExternalOAuthServer toolsets
   *
   * @remarks
   * Change an attached external OAuth server between provider-hosted and Speakeasy-hosted authorization-server metadata without replacing the server, registrations, tokens, or toolset association
   */
  async updateExternalOAuthServer(
    request: UpdateExternalOAuthServerRequest,
    security?: UpdateExternalOAuthServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Toolset> {
    return unwrapAsync(toolsetsUpdateExternalOAuthServer(
      this,
      request,
      security,
      options,
    ));
  }
}
